/**
 * SessionStoragePort — where auth/session material is kept.
 *
 * This is the ports layer (ARCHITECTURE §3). Feature code never touches a
 * storage API directly; it receives a port, and the port is chosen once at
 * startup by `selectSessionStorage()`.
 *
 * WHY THIS EXISTS AT ALL IN A WEB-ONLY SLICE (D-005, INV-T24):
 *
 *   D-005 says, in as many words, that the default web storage mechanism must
 *   NOT be assumed sufficient for native. Native iOS and Android builds must
 *   store session material through an explicitly designed and tested
 *   secure-storage adapter, proven by Risk Probe B before anything depends on
 *   it.
 *
 *   If S-01 handed `window.localStorage` straight to the Supabase client,
 *   there would be no seam to put that adapter behind later — the native build
 *   would inherit the web mechanism by default, silently, and would appear to
 *   work. The port is the seam. It costs a few lines now and makes the native
 *   decision an explicit, reviewable act instead of an omission.
 *
 * THE SECURE NATIVE ADAPTER NOW EXISTS (M2 / S-02A). D-005 is satisfied by
 * `createNativeSecureSessionStorage()` below, which stores session material in
 * the platform secure store — iOS Keychain, Android Keystore-backed encryption
 * — through `@aparajita/capacitor-secure-storage`.
 *
 * THREE BRANCHES, AND ONLY TWO OF THEM PERSIST ANYTHING:
 *
 *   web      → `localStorage`, unchanged since M1 and byte-for-byte compatible
 *   native   → the platform secure store, behind a readiness gate
 *   unknown  → in memory, non-persistent. Fail closed (R1 finding 1)
 *
 * "We could not tell what platform this is" never reaches persistent storage,
 * because the safe answer to an unanswerable question is the one that costs a
 * sign-in rather than the one that might store credentials in the wrong place.
 *
 * THE PORT IS NOW ASYNC-CAPABLE — `T | Promise<T>` — because Capacitor's bridge
 * is asynchronous. The web and memory adapters are synchronous and satisfy it
 * unchanged; a synchronous return is assignable. supabase-js accepts
 * promise-returning storage (`SupportedStorage` in @supabase/auth-js), which is
 * why no feature code changed for this.
 *
 * WHAT REMAINS UNPROVEN, stated so nobody reads more into the tests than is
 * there: the automated suite proves which options are SENT to the plugin and
 * in what order. It proves nothing about real Keychain or hardware-backed
 * Keystore semantics, real-device reinstall, or survival across an OS update.
 * Those are Risk Probe B on physical hardware, and they are still outstanding.
 */

import { KeychainAccess, SecureStorage } from '@aparajita/capacitor-secure-storage';
import { Capacitor } from '@capacitor/core';
import { reportError } from '../lib/monitoring';

export interface SessionStoragePort {
  /** Human-readable adapter name. Surfaced in diagnostics, never in product UI. */
  readonly name: string;
  getItem(key: string): string | null | Promise<string | null>;
  setItem(key: string, value: string): void | Promise<void>;
  removeItem(key: string): void | Promise<void>;
}

/**
 * The browser's own storage, or `null` when this runtime has none.
 *
 * `window.localStorage` is typed as always present, and in a real browser it
 * is. In a test runtime it may not be: under jsdom on a Node build that ships
 * its own experimental Web Storage, the property exists and its VALUE is
 * `undefined`. Normalising that to `null` here means every caller sees one
 * "there is no storage" value instead of two.
 */
function resolveBrowserStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  // `?? null` looks redundant — TypeScript types `window.localStorage` as
  // always present — and it is NOT. The DOM lib describes a real browser; in a
  // test runtime the property can be present with an `undefined` value, and
  // that is the exact difference between this machine and the CI runner. The
  // types are the optimistic description; this line is the observed behaviour.
  return window.localStorage ?? null;
}

/**
 * Web adapter — browser localStorage.
 *
 * Persistence across restarts is a locked requirement (D-005), and on the web
 * localStorage is the ordinary way to get it.
 *
 * Access is wrapped because storage can throw rather than merely be absent:
 * Safari private mode, disabled site data, and quota exhaustion all raise. A
 * throw here would break sign-in entirely, so failures degrade to "no stored
 * session" — the user signs in again — instead of crashing the app.
 *
 * `backing` IS `Storage | null`, NOT `Storage | undefined`, AND THAT IS THE
 * WHOLE POINT (CI failure, 2026-08-11).
 *
 *   A default parameter is applied whenever the argument is `undefined` — so
 *   `createWebSessionStorage(undefined)` does NOT mean "no backing storage",
 *   it means "use the default", which is the ambient `window.localStorage`.
 *   A test written to pass `undefined` was therefore not testing the
 *   no-storage path at all: it was testing whatever the runner happened to
 *   provide. It passed on a runtime where `window.localStorage` was undefined
 *   and failed on one where it worked, which is how CI went red on four
 *   consecutive pushes while every local gate was green.
 *
 *   `null` is not `undefined`, so it does not trigger the default. "There is
 *   no storage here" is now something a caller can actually say.
 */
export function createWebSessionStorage(
  backing: Storage | null = resolveBrowserStorage(),
): SessionStoragePort {
  return {
    name: 'web:localStorage',
    getItem(key) {
      try {
        return backing?.getItem(key) ?? null;
      } catch {
        return null;
      }
    },
    setItem(key, value) {
      try {
        backing?.setItem(key, value);
      } catch {
        // Session simply will not persist. Sign-in still works.
      }
    },
    removeItem(key) {
      try {
        backing?.removeItem(key);
      } catch {
        // Nothing to do — treat as already removed.
      }
    },
  };
}

/**
 * In-memory adapter — used when no browser storage exists at all (tests, SSR).
 * The session lives for the lifetime of the tab and is never written anywhere.
 */
export function createMemorySessionStorage(): SessionStoragePort {
  const map = new Map<string, string>();
  return {
    name: 'memory',
    getItem: (key) => map.get(key) ?? null,
    setItem: (key, value) => void map.set(key, value),
    removeItem: (key) => void map.delete(key),
  };
}

/**
 * Native adapter for M2 / S-01 — DELIBERATELY NON-PERSISTENT.
 *
 * SUPERSEDED BY `createNativeSecureSessionStorage()` IN M2 / S-02A and no longer
 * reachable from `selectSessionStorage()`. Kept because it is the honest
 * fallback if a future slice ever needs a native build with no secure store:
 * a native branch that cannot persist is safe, and one that quietly falls
 * through to `localStorage` is not.
 *
 *   localStorage WORKS inside a Capacitor WebView. If the native branch simply
 *   fell through to `createWebSessionStorage()`, a native build would appear to
 *   behave perfectly — sign in, force-quit, still signed in — while storing
 *   Brandon's session through the exact mechanism D-005 says must NOT be
 *   assumed sufficient for native. The failure would be invisible in every
 *   test, on every device, forever.
 */
export function createNativePendingSessionStorage(): SessionStoragePort {
  const backing = createMemorySessionStorage();
  return {
    ...backing,
    name: 'native:non-persistent-pending-secure-adapter',
  };
}

/* ==========================================================================
 * M2 / S-02A — the real native secure store
 * ========================================================================== */

/** The Crewlink namespace inside the platform secure store. */
export const SECURE_STORAGE_KEY_PREFIX = 'crewlink_session_';

/**
 * The fresh-install marker. ONE fixed, non-secret value.
 *
 * NOTHING ELSE FROM THE SESSION EVER GOES HERE. No token, no refresh token, no
 * email, no password, no user id — on native, `window.localStorage` holds this
 * constant and nothing else, and a test asserts exactly that. The marker's only
 * job is to answer "has this install run before?"; its VALUE carries no
 * information, which is why it is a fixed string rather than anything derived.
 */
export const INSTALL_MARKER_KEY = 'crewlink.install-marker';
export const INSTALL_MARKER_VALUE = 'installed';

/**
 * Whether this install has run before.
 *
 * WHY `localStorage` AND NOT THE SECURE STORE: the marker has to live somewhere
 * an uninstall DELETES. On iOS the Keychain famously survives app deletion, so
 * a marker kept there would answer "yes, seen before" on a fresh install and
 * defeat its own purpose. The WebView's storage lives in the app container and
 * goes with the app.
 *
 * EVERY FAILURE MODE HERE IS SAFE IN THE SAME DIRECTION. If the marker is
 * missing when it should be present — storage cleared, "Offload App", a throw —
 * we purge and the user signs in again. The cost of a false "fresh install" is
 * one sign-in. The cost of a false "seen before" is a resurrected session, and
 * this design cannot produce it.
 */
export interface InstallMarker {
  present(): boolean;
  write(): void;
  clear(): void;
}

export function createLocalStorageInstallMarker(
  backing: Storage | null = resolveBrowserStorage(),
): InstallMarker {
  return {
    present() {
      try {
        return backing?.getItem(INSTALL_MARKER_KEY) === INSTALL_MARKER_VALUE;
      } catch {
        // Cannot tell → treat as a fresh install and purge. Fail closed.
        return false;
      }
    },
    write() {
      try {
        backing?.setItem(INSTALL_MARKER_KEY, INSTALL_MARKER_VALUE);
      } catch (error) {
        // STILL FAIL-SAFE, AND NO LONGER SILENT (R1 finding L1).
        //
        // The behaviour is unchanged and deliberately so: a failed write means
        // the next launch sees no marker, treats the install as fresh and
        // purges again. That costs one sign-in and can never leave a stale
        // session readable, so it must not crash startup.
        //
        // What was wrong was the SILENCE. An app that quietly purges on every
        // launch looks to a user like "it keeps signing me out" and looks to a
        // developer like nothing at all. The report is the difference between
        // a diagnosable symptom and a mystery.
        //
        // The context carries no product or session content — the key and value
        // here are a fixed non-secret constant, and neither is included.
        reportError(error, 'session.installMarker.write');
      }
    },
    clear() {
      try {
        backing?.removeItem(INSTALL_MARKER_KEY);
      } catch {
        // Nothing to do — treat as already gone.
      }
    },
  };
}

/**
 * The slice of `@aparajita/capacitor-secure-storage` this adapter uses.
 *
 * Declared structurally so tests drive a mock through the SAME code path the
 * plugin takes. A test that asserts on an adapter's `name` proves a string; a
 * test that watches these calls proves behaviour, which is what the review
 * asked for.
 *
 * RAW-STRING METHODS ONLY — `setItem`/`getItem`/`removeItem`, never
 * `set`/`get`/`remove`. The latter JSON-stringify on write and parse on read,
 * and Supabase already hands us a JSON *string*; routing it through those would
 * double-encode it.
 */
export interface SecureStorageLike {
  setKeyPrefix(prefix: string): Promise<void>;
  setSynchronize(sync: boolean): Promise<void>;
  setDefaultKeychainAccess(access: KeychainAccess): Promise<void>;
  clear(): Promise<void>;
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

/**
 * Diagnostic context for a secure-store failure, with the plugin's own error
 * kind appended when it supplies one.
 *
 * `missingKey`, `invalidData`, `osError` and `unknownError` mean genuinely
 * different things to whoever reads the report — "we asked wrongly" versus "the
 * OS refused" is the difference between a bug in our code and a device problem.
 * A NORMAL MISSING VALUE NEVER REACHES HERE: the plugin returns `null` for an
 * absent key rather than throwing, so nothing routine is ever reported.
 */
function describeSecureStorageFailure(operation: string, error: unknown): string {
  const base = `session.secureStorage.${operation}`;
  const code: unknown = (error as { code?: unknown } | null)?.code;
  return typeof code === 'string' && code.length > 0 ? `${base}:${code}` : base;
}

/**
 * The real native adapter — platform secure storage, behind ONE readiness gate.
 *
 * THE GATE IS THE WHOLE DESIGN (R1 blocker B1). Everything that must happen
 * before the store is touched happens inside a single promise created at
 * construction, and every operation awaits THAT SAME promise:
 *
 *   1. set the Crewlink key prefix
 *   2. setSynchronize(false)
 *   3. setDefaultKeychainAccess(afterFirstUnlockThisDeviceOnly)
 *   4. inspect the install marker
 *   5. if absent — clear the Crewlink namespace, then write the marker
 *
 * There is NO separate startup initializer and NO dependence on when the
 * Supabase client is constructed. Supabase may call `getItem` on its first
 * tick; the call queues behind the gate and cannot reach the store early. That
 * is what makes "a reinstall can never resurrect a session" a property of the
 * code rather than a promise about call order.
 *
 * STEPS 1–3 ARE NOT DECORATION, AND THEIR PLACE IN THE GATE IS LOAD-BEARING.
 * Verified in the published plugin: `setKeyPrefix`, `setSynchronize` and
 * `setDefaultKeychainAccess` mutate JAVASCRIPT-side state on the plugin proxy,
 * which each `setItem` reads at call time. The constructor default is
 * `KeychainAccess.whenUnlocked` and the prefix is a generic shared one — so a
 * write that beat these calls would be stored with the wrong accessibility, in
 * the wrong namespace. They are ordering-sensitive for exactly the same reason
 * the purge is, and the same gate covers both.
 *
 * THE GATE NEVER REJECTS. `.catch` is attached at creation, so there is no
 * window in which an unobserved rejection could escape; a failure sets degraded
 * mode instead and the promise every operation awaits resolves safely.
 */
export function createNativeSecureSessionStorage(
  plugin: SecureStorageLike,
  marker: InstallMarker = createLocalStorageInstallMarker(),
): SessionStoragePort {
  let degraded = false;

  const ready: Promise<void> = (async () => {
    await plugin.setKeyPrefix(SECURE_STORAGE_KEY_PREFIX);

    // iOS only — a documented no-op on Android and web. Called explicitly
    // rather than trusting the plugin's default, so the decision is visible.
    await plugin.setSynchronize(false);
    await plugin.setDefaultKeychainAccess(KeychainAccess.afterFirstUnlockThisDeviceOnly);

    if (marker.present()) return;

    // Fresh install. The purge runs BEFORE anything can read, and it is scoped
    // to the Crewlink prefix set in step 1 — so it can only ever clear our own
    // namespace, never another consumer's.
    try {
      await plugin.clear();
    } catch (error) {
      // The one failure that must fail hardest: if the purge did not happen, a
      // read could hand back the previous install's session. Degrade for the
      // whole run rather than risk it.
      degraded = true;
      reportError(error, describeSecureStorageFailure('purge', error));
      return;
    }

    marker.write();
  })().catch((error: unknown) => {
    // Attached at creation — the promise above can never reject unobserved.
    degraded = true;
    reportError(error, describeSecureStorageFailure('ready', error));
  });

  return {
    name: 'native:secure-storage',

    async getItem(key) {
      await ready;
      if (degraded) return null;

      try {
        return await plugin.getItem(key);
      } catch (error) {
        // NEVER a partial or stale session, and never a throw into Supabase's
        // initialisation. An unreadable store means "signed out", which costs
        // one sign-in; anything else risks acting on a session we cannot trust.
        reportError(error, describeSecureStorageFailure('read', error));
        return null;
      }
    },

    async setItem(key, value) {
      await ready;
      if (degraded) return;

      try {
        await plugin.setItem(key, value);
      } catch (error) {
        // Swallowed deliberately, and NOT written anywhere less safe. The user
        // stays signed in for this run and signs in again next launch — the
        // failure costs a sign-in rather than weakening where credentials live.
        reportError(error, describeSecureStorageFailure('write', error));
      }
    },

    async removeItem(key) {
      await ready;

      // THE INVARIANT, STATED ONCE AND ENFORCED ON BOTH PATHS BELOW:
      //
      //   If secure removal cannot be CONFIRMED, for any reason at all, the
      //   next launch must force the fresh-install purge before any persisted
      //   auth can be read.
      //
      // "Any reason" includes degraded mode, which is why this is not simply an
      // early return (R1 finding L2). Degraded means readiness or the purge
      // failed, so the store may well still hold a token — and returning
      // quietly would leave that token readable on the next launch with the
      // marker still in place. The secure removal stays a no-op because there
      // is nothing trustworthy to remove it with; dropping the marker is what
      // makes the sign-out stick anyway.
      if (degraded) {
        marker.clear();
        return;
      }

      try {
        await plugin.removeItem(key);
      } catch (error) {
        reportError(error, describeSecureStorageFailure('remove', error));

        // Same invariant, the other way in: if the token survived because the
        // delete failed, the next launch must not treat it as valid. Dropping
        // the marker makes that launch look like a fresh install, which forces
        // the namespace purge before any auth read. Sign-out therefore holds
        // even when the secure store refuses — one launch later at the worst.
        marker.clear();
      }
    },
  };
}

/**
 * Adapter for the case where we could not determine the platform at all —
 * DELIBERATELY NON-PERSISTENT, for the same reason the native one is.
 *
 * Distinct from `memory` and from the native adapter on purpose. "We chose
 * memory because there is no storage here" and "we chose memory because the
 * platform question threw" are different facts, and only one of them is a bug
 * worth chasing. A diagnostic that collapses them wastes the next person's
 * afternoon.
 */
export function createUndeterminedPlatformSessionStorage(): SessionStoragePort {
  const backing = createMemorySessionStorage();
  return {
    ...backing,
    name: 'memory:platform-undetermined',
  };
}

/** What the runtime is, including "we could not tell". */
export type PlatformDetection = 'web' | 'native' | 'unknown';

/**
 * Which platform we are running on.
 *
 * `src/platform/` is the ONLY place `@capacitor/*` may be imported
 * (ARCHITECTURE §3, INV-T20), and this is that import.
 *
 * THREE-VALUED ON PURPOSE — this is R1 finding 1 (2026-08-11).
 *
 * An earlier version returned a boolean and answered `false` when detection
 * threw. "False" means "this is the web", and the web branch is the PERSISTENT
 * `localStorage` branch — so a detection failure inside a native shell would
 * have selected browser persistence for session credentials. That is precisely
 * the outcome D-005 and INV-T24 forbid, arrived at through the error path
 * instead of the happy path, and the comment above it claimed the opposite.
 *
 * A boolean cannot express "we do not know", so it was the wrong shape for the
 * question. `unknown` is now its own answer and its own branch.
 */
export function detectPlatform(): PlatformDetection {
  try {
    return Capacitor.isNativePlatform() ? 'native' : 'web';
  } catch {
    return 'unknown';
  }
}

/**
 * Chosen once at startup.
 *
 * ORDER MATTERS. The platform is resolved FIRST, before the localStorage probe,
 * because a Capacitor WebView has a perfectly working `window.localStorage` —
 * so a native platform would otherwise satisfy the web branch and silently take
 * it. The platform question has to be asked before the storage question.
 *
 * ONLY A CONFIRMED WEB RUNTIME REACHES PERSISTENT STORAGE. Native and unknown
 * both land on a store that cannot persist. The rule is easier to state than to
 * remember in the negative: persistence is opt-in for one known-safe case,
 * rather than the default that every other case has to escape from.
 *
 * The deliberate cost, stated so nobody has to rediscover it: if detection ever
 * threw in a real browser, that user would sign in again on every launch. That
 * is the correct trade — an extra sign-in against storing credentials through a
 * mechanism the product has decided is not safe for the platform we may
 * actually be on. In practice the cost is close to zero, because
 * `Capacitor.isNativePlatform()` is a synchronous property read with nothing in
 * it that fails; the branch exists for the case we cannot foresee, which is the
 * only kind of case a fail-closed branch is ever for.
 *
 * The real secure adapter is added in this same function in M2 / S-02, behind
 * Risk Probe B, and nothing in feature code changes when it is.
 */
export function selectSessionStorage(): SessionStoragePort {
  const platform = detectPlatform();

  if (platform === 'native') {
    return createNativeSecureSessionStorage(SecureStorage);
  }
  if (platform === 'unknown') {
    // Fail closed. Never the web branch — we may well be native.
    return createUndeterminedPlatformSessionStorage();
  }

  // Confirmed web, and only confirmed web, from here down.
  if (typeof window === 'undefined' || !('localStorage' in window)) {
    return createMemorySessionStorage();
  }
  return createWebSessionStorage();
}
